
/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { map, distinctUntilChanged, debounceTime, startWith } from 'rxjs/operators';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UserApi, CommonApi, UniversalTrackerApi, InitiativeApi, InitiativeGroupApi } from '../../../shared/sdk/services';
import { NotificationService } from '../../../shared/services/notification.service';
import { Observable } from 'rxjs';
import { OnboardingListApi } from '../../sdk/services/custom/OnboardingListApi';
import { ValueListApi } from 'app/shared/sdk/services/custom/ValueListApi';
import { Issuer, ValueList } from 'app/shared/lib/wwg.classes';

interface AutocompleteItem {
  _id: string;
  name: string;
  displayString: {
    code: string,
    value: string
  };
  searchString: string;
}

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss']
})
export class AutocompleteComponent implements OnInit {

  public searchCtrl = new FormControl();
  public filteredItems: Observable<AutocompleteItem[]>;
  public items: AutocompleteItem[] = [];
  public itemId: string;
  public item: AutocompleteItem;

  // Direct array link
  public selectedItems: any[];

  // Extended view data list
  public selectedItemsDetails: any[];

  @Input() public ref: string;
  @Input() public dataType: string;
  @Input() public placeholder: string;
  @Input() public canDelete: boolean = true;
  @Input() public disabled: boolean;
  @Input() public required: boolean = true;
  @Input() public modelProperty: string = '_id';

  @Input() get model() {
    return this.itemId;
  }

  @Input() get models() {
    return this.selectedItems;
  }

  @Input() get selectedObject() {
    return this.item;
  }

  @Output() modelChange = new EventEmitter();
  @Output() selectedObjectChange = new EventEmitter();

  constructor(
    private userApi: UserApi,
    private universalTrackerApi: UniversalTrackerApi,
    private initiativeApi: InitiativeApi,
    private initiativeGroupApi: InitiativeGroupApi,
    private valueListApi: ValueListApi,
    private onboardingListApi: OnboardingListApi,
    private notificationService: NotificationService,
    private commonApi: CommonApi
  ) {
    this.filteredItems = this.searchCtrl.valueChanges.pipe(
      startWith(null),
      debounceTime(200),
      distinctUntilChanged(),
      map(val => this.filterItems(val)));
  }

  public removeValue(index) {
    if (this.required) {
      alert('Cannot remove as this is a required field');
      return;
    }

    if (this.selectedItems) {
      this.selectedItems.splice(index, 1);
    }
    if (this.selectedItemsDetails) {
      this.selectedItemsDetails.splice(index, 1);
    }
    this.model = undefined;
  }

  onChange(item) {
    if (this.selectedItems) {
      if (!this.selectedItems.includes(item[this.modelProperty])) {
        this.selectedItems.push(item[this.modelProperty]);
        this.selectedItemsDetails.push(item);
      }
      this.searchCtrl.reset();
    } else {
      this.model = item[this.modelProperty];
      this.selectedObject = item;
    }
  }

  filterItems(val) {
    if (!val) {
      if (this.selectedItems) {
        return this.items.filter(i => !this.selectedItems.includes(i));
      }

      // this.model = undefined;
      return this.items;
    }
    return this.items.filter(item => {
      const regex = new RegExp(`${val}`, 'gi');
      const match = regex.test(item.searchString);

      return match && (this.selectedItems ? !this.selectedItems.includes(item[this.modelProperty]) : true);
    });
  }

  getId() {
    return this.itemId;
  }

  set model(val) {
    this.itemId = val;
    this.modelChange.emit(this.itemId);
    if (val === undefined || val === null) {
      this.searchCtrl.reset();
    }
  }

  set selectedObject(val) {
    this.item = val;
    this.selectedObjectChange.emit(this.item);
  }

  set models(value) {
    this.selectedItems = value;
  }

  ngOnInit() {
    let api;
    let getSearchString = (item) => item.code + ' ' + item.name;
    let getDisplayString = (item) => ({ code: item.code, value: item.name });
    let getItemMap = (response: any) => response.data;

    switch (this.dataType) {
      case 'initiativeGroup':
        api = this.initiativeGroupApi.list();
        break;
      case 'initiative':
        api = this.initiativeApi.list();
        break;
      case 'portfolio':
        api = this.initiativeApi.listPortfolios();
        break;
      case 'companies':
        api = this.initiativeApi.listCompanies();
        break;
      case 'initiativeNodes':
        api = this.initiativeApi.listInitiativeNodes();
        break;
      case 'onboardingList':
        api = this.onboardingListApi.list();
        break;
      case 'universalTracker':
        api = this.universalTrackerApi.list();
        break;
      case 'user':
        api = this.userApi.list();
        getSearchString = (item) => item.email + ' ' + item.name;
        getDisplayString = (item) => { return { code: item.email, value: item.name }; };
        break;
      case 'valueList':
        api = this.valueListApi.list();
        getSearchString = (item: ValueList) => item.code + ' ' + item.name;
        getDisplayString = (item: ValueList) => ({ code: item.code, value: item.name });
        break;
      case 'sgx_issuer_name':
        api = this.commonApi.getIssuers('SGX4SGX');
        getSearchString = (item: Issuer) => item.name;
        getDisplayString = (item: Issuer) => ({ code: item.name, value: item.name });
        getItemMap = (response: any) => response.data.issuers;
    }

    api.toPromise().then((response: any) => {
      const itemMap = getItemMap(response);
      this.items = itemMap.map((item) => {
        item.searchString = getSearchString(item);
        item.displayString = getDisplayString(item);
        return item;
      });

      if (this.model) {
        const currentItem = this.items.find((i) => i[this.modelProperty] === this.model);
        if (currentItem) {
          this.searchCtrl.setValue(currentItem.name);
        }
      }

      if (Array.isArray(this.models)) {
        this.selectedItemsDetails = this.models.map((item) => {
          const currentItem = this.items.find((i) => i[this.modelProperty] === item);
          return currentItem || { [this.modelProperty]: item, displayString: { code: item, value: 'Not found' } };
        });
      }
    }).catch(this.notificationService.errorHandler);
  }
}
